import type {AddTaskFormPayload} from "./add-task.schema";
import type {MutationCallbacks} from "../../../../types/callbacks";
import type {Task} from "@project/sdk/admin";

import {useMutation, useQueryClient} from "@tanstack/react-query";

import {userKeys} from "../../../../user-keys";

import {addTaskMutationOptions} from "./add-task.mutation-options";

/**
 * Command hook: Add a task to a user.
 *
 * DEFAULT BEHAVIOR (always applied):
 * - Invalidate the user detail query so the new task appears
 *
 * USER CALLBACKS (optional, applied after defaults):
 * - onSuccess: e.g., show toast, close modal
 * - onError: e.g., show error toast
 *
 * @param userId - The ID of the user to add the task to
 * @param callbacks - Optional callbacks for custom logic
 *
 * @example
 * const mutation = useAddTask(userId, {
 *   onSuccess: (task) => showToast(`Task "${task.title}" added`),
 *   onError: (err) => showToast(`Error: ${err.message}`),
 * });
 */
export function useAddTask(
  userId: string,
  callbacks?: Partial<MutationCallbacks<AddTaskFormPayload, Task>>,
) {
  const queryClient = useQueryClient();

  return useMutation({
    ...addTaskMutationOptions(userId),

    onSuccess: (task) => {
      // ─── DEFAULT: Invalidate user detail so tasks are refreshed ───
      void queryClient.invalidateQueries({queryKey: userKeys.detail(userId)});

      // ─── USER: Custom onSuccess callback ──────────────────────────
      callbacks?.onSuccess?.(task);
    },

    onError: (err, vars, ctx) => {
      // ─── USER: Custom onError callback ───────────────────────────
      callbacks?.onError?.(err, vars, ctx);
    },
  });
}
